/**
 * Type-safe, hierarchical query key factory.
 *
 * All React Query keys MUST be defined here — never use raw string arrays.
 * Keys are hierarchical so you can invalidate broadly (e.g., guilds.all)
 * or narrowly (e.g., guilds.detail('gld_1')).
 *
 * MIGRATION IN PROGRESS — per-arche namespaces are moving into their
 * arche's own `api/queryKeys.ts`. The central object below is becoming a
 * thin re-export aggregator. See
 * docs/audits/social-media-modularity-audit.md §10 PW-D. Social-media has
 * been migrated as the pilot; other arches will follow.
 */
import { socialMediaQueryKeys } from '@/arche/arches/social-media/api/queryKeys';
import { forgeQueryKeys } from '@/arche/arches/forge/api/queryKeys';

/**
 * Stable key segment for the email "More filters" dimensions (reply state,
 * address-book membership, age bounds). One segment rather than four so the
 * common no-advanced-filter case stays a single short literal.
 */
function advancedFilterKey(opts?: {
  answered?: boolean;
  inAddressBook?: boolean;
  receivedAfter?: string;
  receivedBefore?: string;
}): string {
  if (!opts) return 'no-advanced';
  const { answered, inAddressBook, receivedAfter, receivedBefore } = opts;
  if (answered === undefined && inAddressBook === undefined && !receivedAfter && !receivedBefore) {
    return 'no-advanced';
  }
  return JSON.stringify({ answered, inAddressBook, receivedAfter, receivedBefore });
}

export const queryKeys = {
  // === Guilds ===
  guilds: {
    all: ['guilds'] as const,
    lists: () => [...queryKeys.guilds.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.guilds.lists(), filters] as const,
    details: () => [...queryKeys.guilds.all, 'detail'] as const,
    detail: (guildId: string) => [...queryKeys.guilds.details(), guildId] as const,
    members: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'members'] as const,
    /** Guild-DM member picker. Distinct from `members` because that key is
     * owned by useGuildMembers' useInfiniteQuery — TanStack v5 forbids sharing
     * one key between useQuery and useInfiniteQuery cache shapes. */
    memberPicker: (guildId: string) =>
      [...queryKeys.guilds.detail(guildId), 'member-picker'] as const,
    channels: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'channels'] as const,
    activity: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'activity'] as const,
    invites: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'invites'] as const,
    bans: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'bans'] as const,
    treasury: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'treasury'] as const,
    my: () => [...queryKeys.guilds.all, 'my'] as const,
    roles: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'roles'] as const,
    permissions: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'permissions'] as const,
  },

  // === Game Rewards ===
  gameRewards: {
    all: ['gameRewards'] as const,
    gameConfigs: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.gameRewards.all, 'gameConfigs', filters] as const)
        : ([...queryKeys.gameRewards.all, 'gameConfigs'] as const),
    subscription: () => [...queryKeys.gameRewards.all, 'subscription'] as const,
    subscriptionTiers: () => [...queryKeys.gameRewards.all, 'subscriptionTiers'] as const,
    timePlayed: () => [...queryKeys.gameRewards.all, 'timePlayed'] as const,
    rewardHistory: () => [...queryKeys.gameRewards.all, 'rewardHistory'] as const,
    rewardLog: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.gameRewards.all, 'rewardLog', filters] as const)
        : ([...queryKeys.gameRewards.all, 'rewardLog'] as const),
    rewardRules: () => [...queryKeys.gameRewards.all, 'rewardRules'] as const,
    rewardTypeLimits: () => [...queryKeys.gameRewards.all, 'rewardTypeLimits'] as const,
    gameRewardLimits: (gameConfigId?: string) =>
      [...queryKeys.gameRewards.all, 'gameRewardLimits', gameConfigId ?? 'all'] as const,
    leaderboard: (gameConfigId: string, gameEvent: string) =>
      [...queryKeys.gameRewards.all, 'leaderboard', gameConfigId, gameEvent] as const,
    leaderboardGames: () => [...queryKeys.gameRewards.all, 'leaderboardGames'] as const,
    timeGames: () => [...queryKeys.gameRewards.all, 'timeGames'] as const,
    timeLeaderboard: (gameConfigId: string) =>
      [...queryKeys.gameRewards.all, 'timeLeaderboard', gameConfigId] as const,
    rewardTypes: (gameConfigId?: string) =>
      [...queryKeys.gameRewards.all, 'rewardTypes', gameConfigId ?? null] as const,
    rewardLeaderboard: (rewardType: string, gameConfigId?: string) =>
      [
        ...queryKeys.gameRewards.all,
        'rewardLeaderboard',
        rewardType,
        gameConfigId ?? null,
      ] as const,
    leaderboardAnonymity: () => [...queryKeys.gameRewards.all, 'leaderboardAnonymity'] as const,
    adminSubscriptions: () => [...queryKeys.gameRewards.all, 'adminSubscriptions'] as const,
    claimRejections: (filters?: Record<string, unknown>) =>
      [...queryKeys.gameRewards.all, 'claimRejections', filters ?? {}] as const,
    itemDefsByIds: (ids: readonly string[]) =>
      [...queryKeys.gameRewards.all, 'itemDefsByIds', [...ids].sort().join(',')] as const,
  },

  // === Chat ===
  chat: {
    all: ['chat'] as const,
    channels: () => [...queryKeys.chat.all, 'channels'] as const,
    channel: (channelId: string) => [...queryKeys.chat.channels(), channelId] as const,
    channelMembers: (channelId: string) =>
      [...queryKeys.chat.channel(channelId), 'members'] as const,
    settings: () => [...queryKeys.chat.all, 'settings'] as const,
    blocks: () => [...queryKeys.chat.all, 'blocks'] as const,
    mentions: () => [...queryKeys.chat.all, 'mentions'] as const,
    /**
     * `filters` is the serialized sender / match / has / date filter set
     * (`chatSearchFiltersKey`), so two searches with the same text but
     * different filters never share a cache entry.
     */
    messageSearch: (channelId: string, query: string, filters: string) =>
      [...queryKeys.chat.channel(channelId), 'messageSearch', query, filters] as const,
    globalMessageSearch: (query: string, filters: string) =>
      [...queryKeys.chat.all, 'globalMessageSearch', query, filters] as const,
    conversationSearch: (query: string) =>
      [...queryKeys.chat.all, 'conversationSearch', query] as const,
    peopleSearch: (query: string) => [...queryKeys.chat.all, 'peopleSearch', query] as const,
  },

  // === DFE ===
  dfe: {
    all: ['dfe'] as const,
    definitions: () => [...queryKeys.dfe.all, 'definitions'] as const,
    definitionList: (filters: Record<string, unknown>) =>
      [...queryKeys.dfe.definitions(), filters] as const,
    definition: (itemDefId: string) => [...queryKeys.dfe.definitions(), itemDefId] as const,
    inventory: (filters?: Record<string, unknown>) =>
      [...queryKeys.dfe.all, 'inventory', filters] as const,
    inventoryTotals: (filters?: Record<string, unknown>) =>
      [...queryKeys.dfe.all, 'inventoryTotals', filters] as const,
    transferHistory: (filters?: Record<string, unknown>) =>
      [...queryKeys.dfe.all, 'transferHistory', filters] as const,
    adminTransfers: (filters?: Record<string, unknown>) =>
      [...queryKeys.dfe.all, 'adminTransfers', filters] as const,
  },

  // === LitSquad ===
  litSquad: {
    all: ['litSquad'] as const,
    definitions: () => [...queryKeys.litSquad.all, 'definitions'] as const,
    definitionList: (filters: Record<string, unknown>) =>
      [...queryKeys.litSquad.definitions(), filters] as const,
    definition: (itemDefId: string) => [...queryKeys.litSquad.definitions(), itemDefId] as const,
    inventory: (filters?: Record<string, unknown>) =>
      [...queryKeys.litSquad.all, 'inventory', filters] as const,
    item: (itemId: string) => [...queryKeys.litSquad.all, 'item', itemId] as const,
    transferHistory: (filters?: Record<string, unknown>) =>
      [...queryKeys.litSquad.all, 'transferHistory', filters] as const,
    adminTransfers: (filters?: Record<string, unknown>) =>
      [...queryKeys.litSquad.all, 'adminTransfers', filters] as const,
    avatar: () => [...queryKeys.litSquad.all, 'avatar'] as const,
    craft: () => [...queryKeys.litSquad.all, 'craft'] as const,
    craftIngredients: () => [...queryKeys.litSquad.craft(), 'ingredients'] as const,
  },

  // === DFE Game State ===
  gameState: {
    all: ['gameState'] as const,
    detail: (keyword: string) => [...queryKeys.gameState.all, 'detail', keyword] as const,
    list: () => [...queryKeys.gameState.all, 'list'] as const,
    admin: () => [...queryKeys.gameState.all, 'admin'] as const,
    adminDetail: (keyword: string) => [...queryKeys.gameState.admin(), keyword] as const,
    adminList: (filters?: Record<string, unknown>) =>
      [...queryKeys.gameState.admin(), 'list', filters] as const,
    adminAuditLog: (filters?: Record<string, unknown>) =>
      [...queryKeys.gameState.admin(), 'auditLog', filters] as const,
    grants: () => [...queryKeys.gameState.admin(), 'grants'] as const,
    grantsList: (filters?: Record<string, unknown>) =>
      [...queryKeys.gameState.grants(), 'list', filters] as const,
    grantDetail: (archeId: string, resourceType: string, resourceName: string) =>
      [...queryKeys.gameState.grants(), 'detail', archeId, resourceType, resourceName] as const,
    configs: () => [...queryKeys.gameState.admin(), 'configs'] as const,
    configsList: (filters?: Record<string, unknown>) =>
      [...queryKeys.gameState.configs(), 'list', filters] as const,
    configDetail: (keyword: string) =>
      [...queryKeys.gameState.configs(), 'detail', keyword] as const,
  },

  // === Marketplace arche records (shared; icon/name/pricing metadata) ===
  marketplaceArche: {
    all: ['marketplaceArche'] as const,
    detail: (archeId: string) => [...queryKeys.marketplaceArche.all, 'detail', archeId] as const,
  },

  // === The public Games page (ADR-035) ===
  games: {
    all: ['games'] as const,
    home: () => [...queryKeys.games.all, 'home'] as const,
    genres: () => [...queryKeys.games.all, 'genres'] as const,
    genre: (genreId: string, sort: string) =>
      [...queryKeys.games.all, 'genre', genreId, sort] as const,
    search: (q: string) => [...queryKeys.games.all, 'search', q] as const,
    detail: (slug: string) => [...queryKeys.games.all, 'detail', slug] as const,
    arche: (archeKey: string) => [...queryKeys.games.all, 'arche', archeKey] as const,
    mostPlayed: () => [...queryKeys.games.all, 'mostPlayed'] as const,
    adminFeatured: () => [...queryKeys.games.all, 'adminFeatured'] as const,
    adminSwitch: () => [...queryKeys.games.all, 'adminSwitch'] as const,
    adminHidden: () => [...queryKeys.games.all, 'adminHidden'] as const,
    adminGenreAllowances: () => [...queryKeys.games.all, 'adminGenreAllowances'] as const,
  },

  // === The public Arche Store (/a/store) ===
  store: {
    all: ['store'] as const,
    home: () => [...queryKeys.store.all, 'home'] as const,
    categories: () => [...queryKeys.store.all, 'categories'] as const,
    apps: (category: string | null, sort: string) =>
      [...queryKeys.store.all, 'apps', category ?? 'all', sort] as const,
    top: (category: string | null) => [...queryKeys.store.all, 'top', category ?? 'all'] as const,
    app: (slug: string) => [...queryKeys.store.all, 'app', slug] as const,
    story: (slug: string) => [...queryKeys.store.all, 'story', slug] as const,
    search: (q: string) => [...queryKeys.store.all, 'search', q] as const,
    suggestions: () => [...queryKeys.store.all, 'suggestions'] as const,
    address: (archId: string, listingId: string | null) =>
      [...queryKeys.store.all, 'address', archId, listingId ?? ''] as const,
    adminSwitch: () => [...queryKeys.store.all, 'adminSwitch'] as const,
    adminPicker: () => [...queryKeys.store.all, 'adminPicker'] as const,
    adminFeatured: () => [...queryKeys.store.all, 'adminFeatured'] as const,
    adminHidden: () => [...queryKeys.store.all, 'adminHidden'] as const,
    adminCollections: () => [...queryKeys.store.all, 'adminCollections'] as const,
    adminSuggestions: () => [...queryKeys.store.all, 'adminSuggestions'] as const,
    adminUncategorized: () => [...queryKeys.store.all, 'adminUncategorized'] as const,
    adminScreenshotReviews: () => [...queryKeys.store.all, 'adminScreenshotReviews'] as const,
    adminArcheScreenshots: (archId: string) =>
      [...queryKeys.store.all, 'adminArcheScreenshots', archId] as const,
  },

  // === An arche's page metadata (GET /arches/:id/metadata — what its own page reads) ===
  archeMetadata: {
    all: ['archeMetadata'] as const,
    detail: (archeId: string) => [...queryKeys.archeMetadata.all, 'detail', archeId] as const,
  },

  // === DFE Inventory Transition ===
  inventoryTransition: {
    all: ['inventoryTransition'] as const,
    importHistory: () => [...queryKeys.inventoryTransition.all, 'importHistory'] as const,
    adminImports: () => [...queryKeys.inventoryTransition.all, 'adminImports'] as const,
  },

  // === Crafting ===
  crafting: {
    all: ['crafting'] as const,
    recipes: (filters?: Record<string, unknown>) =>
      [...queryKeys.crafting.all, 'recipes', filters] as const,
    recipeDetail: (recipeId: string) => [...queryKeys.crafting.all, 'recipe', recipeId] as const,
    learnedRecipes: (userId?: string) =>
      [...queryKeys.crafting.all, 'learnedRecipes', userId] as const,
    log: (filters?: Record<string, unknown>) =>
      [...queryKeys.crafting.all, 'log', filters] as const,
  },

  // === Favorites ===
  favorites: {
    all: ['favorites'] as const,
    list: () => [...queryKeys.favorites.all, 'list'] as const,
    status: (archId: string) => [...queryKeys.favorites.all, 'status', archId] as const,
    /** Display metadata (icons/names) for the "Add Featured" catalog arches. */
    featuredMeta: () => [...queryKeys.favorites.all, 'featured-meta'] as const,
  },

  // === Plugin file-intake destinations (ADR-030) ===
  fileIntake: {
    all: ['file-intake'] as const,
    list: () => [...queryKeys.fileIntake.all, 'list'] as const,
  },

  // === Notifications (cross-arche badge summary) ===
  notifications: {
    all: ['notifications'] as const,
    summary: () => [...queryKeys.notifications.all, 'summary'] as const,
  },

  // === Polls ===
  polls: {
    all: ['polls'] as const,
    list: (filters?: { status?: string }) =>
      [...queryKeys.polls.all, 'list', { status: filters?.status ?? null }] as const,
    detail: (pollId: string) => [...queryKeys.polls.all, 'detail', pollId] as const,
    results: (pollId: string) => [...queryKeys.polls.detail(pollId), 'results'] as const,
    myResponse: (pollId: string) => [...queryKeys.polls.detail(pollId), 'myResponse'] as const,
  },

  // === Liquidity Caches ===
  liquidityCaches: {
    all: ['liquidityCaches'] as const,
    pools: () => [...queryKeys.liquidityCaches.all, 'pools'] as const,
    pool: (poolId: string) => [...queryKeys.liquidityCaches.pools(), poolId] as const,
    positions: () => [...queryKeys.liquidityCaches.all, 'positions'] as const,
    quote: (poolId: string, params: Record<string, unknown>) =>
      [...queryKeys.liquidityCaches.pool(poolId), 'quote', params] as const,
  },

  // === Plugin Builder ===
  pluginBuilder: {
    all: ['pluginBuilder'] as const,
    sessions: () => [...queryKeys.pluginBuilder.all, 'sessions'] as const,
    publishStatus: (sessionId: string) =>
      [...queryKeys.pluginBuilder.all, 'publishStatus', sessionId] as const,
    invocationFailures: (sessionId: string) =>
      [...queryKeys.pluginBuilder.all, 'invocationFailures', sessionId] as const,
  },

  // === Plugin submissions (dev console) ===
  submissions: {
    all: ['submissions'] as const,
    myList: () => [...queryKeys.submissions.all, 'mine'] as const,
    detail: (submissionId: string) =>
      [...queryKeys.submissions.all, 'detail', submissionId] as const,
    escalated: () => [...queryKeys.submissions.all, 'admin', 'escalated'] as const,
    escalatedCount: () => [...queryKeys.submissions.all, 'admin', 'escalated', 'count'] as const,
    /** Store listings waiting for a person (ADR-035 §12). */
    storeListingReviews: () => [...queryKeys.submissions.all, 'admin', 'store-listings'] as const,
    /** Shared pictures waiting for a person, and open reports (Community Assets). */
    communityPictureReviews: () =>
      [...queryKeys.submissions.all, 'admin', 'community-pictures'] as const,
    communityPictureReports: () =>
      [...queryKeys.submissions.all, 'admin', 'community-picture-reports'] as const,
  },

  // === Contributor portal — arche detail ===
  contributorArches: {
    all: ['contributor', 'arches'] as const,
    detail: (archeId: string) => [...queryKeys.contributorArches.all, 'detail', archeId] as const,
    libraryConfig: (archeId: string) =>
      [...queryKeys.contributorArches.detail(archeId), 'library-config'] as const,
    billingSummary: (archeId: string) =>
      [...queryKeys.contributorArches.detail(archeId), 'billing-summary'] as const,
    /** Content-pack storage + bandwidth bill (ADR-021 §11). */
    contentBilling: (archeId: string) =>
      [...queryKeys.contributorArches.detail(archeId), 'content-billing'] as const,
    /** Picture sharing (Community Assets): the switch, usage and rates. */
    communitySharing: (archeId: string) =>
      [...queryKeys.contributorArches.detail(archeId), 'community-sharing'] as const,
    /** Extra Store listings, their slots and the next slot's price (ADR-035). */
    storeListings: (archeId: string) =>
      [...queryKeys.contributorArches.detail(archeId), 'store-listings'] as const,
    // Nested UNDER detail(archeId), like its siblings on the same page, so
    // invalidating the arche detail refreshes this section too. It previously
    // sat under `pluginBuilder`, which keyed a contributor-arche resource off
    // an unrelated namespace: the obvious refresh after a mutation would have
    // updated Assets and Documentation while leaving these counts stale, with
    // nothing on screen to explain why.
    invocationFailures: (archeId: string) =>
      [...queryKeys.contributorArches.detail(archeId), 'invocation-failures'] as const,
    assets: (archeId: string, opts?: Record<string, unknown>) =>
      [...queryKeys.contributorArches.detail(archeId), 'assets', opts ?? {}] as const,
    /** Workspace (org Holder) libraries this arche is linked to + the caller's linkable ones. */
    workspaces: (archeId: string) =>
      [...queryKeys.contributorArches.detail(archeId), 'workspaces'] as const,
    /** The arche's own active assets plus the shared assets a publish would pin. */
    sharedAssets: (archeId: string, opts?: Record<string, unknown>) =>
      [...queryKeys.contributorArches.detail(archeId), 'shared-assets', opts ?? {}] as const,
    /**
     * Who owns the arche, and who may use it (ADR-026). Nested here like the
     * page's other sections: an ownership change also moves the payer, so the
     * billing sections beside it must refresh with it. The Company page's
     * Access panel reads the same entry.
     */
    ownership: (archeId: string) =>
      [...queryKeys.contributorArches.detail(archeId), 'ownership'] as const,
    audience: (archeId: string) =>
      [...queryKeys.contributorArches.detail(archeId), 'audience'] as const,
  },

  // === Admin — Fias-owned (platform-owned) plugin arches ===
  // Deliberately NOT nested under `contributorArches`: a different authority
  // (`requireAdmin` on /v1/admin/plugins) and a different invalidation lifetime.
  // Granting yourself a maintainer row invalidates BOTH this namespace and
  // `contributorArches.workspaces(archeId)` — the grant is exactly what flips
  // the arche's workspace-link route from 403 to 200.
  // === Admin — Companies: Fias-granted capabilities + email oversight (ADR-023) ===
  adminCompanies: {
    all: ['admin', 'companies'] as const,
    domains: () => [...queryKeys.adminCompanies.all, 'domains'] as const,
    killSwitch: () => [...queryKeys.adminCompanies.all, 'killSwitch'] as const,
    grants: (entitlement: string) =>
      [...queryKeys.adminCompanies.all, 'grants', entitlement] as const,
    companySearch: (q: string) => [...queryKeys.adminCompanies.all, 'companySearch', q] as const,
    webHosts: () => [...queryKeys.adminCompanies.all, 'webHosts'] as const,
    webHost: (hostId: string) => [...queryKeys.adminCompanies.all, 'webHost', hostId] as const,
    webKillSwitch: () => [...queryKeys.adminCompanies.all, 'webKillSwitch'] as const,
  },

  adminPlatformPlugins: {
    all: ['admin', 'platformPlugins'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.adminPlatformPlugins.all, 'list', filters ?? {}] as const,
    maintainers: (archeId: string) =>
      [...queryKeys.adminPlatformPlugins.all, 'maintainers', archeId] as const,
    // Developer plugins the Dev Console can annex (arches-summary, kind=plugin).
    // Under this prefix so a successful annex invalidates it with the rest.
    annexCandidates: (search: string) =>
      [...queryKeys.adminPlatformPlugins.all, 'annex-candidates', search] as const,
    billingAccount: () => [...queryKeys.adminPlatformPlugins.all, 'billing-account'] as const,
    fiasArcheRoles: () => [...queryKeys.adminPlatformPlugins.all, 'fias-arche-roles'] as const,
  },

  // === Fias arche roles — which arche a platform link opens (ADR-037 D8) ===
  fiasArches: {
    all: ['fiasArches'] as const,
    role: (role: string) => [...queryKeys.fiasArches.all, role] as const,
  },

  // === Contributor portal — Workspace (org Holder) shared asset library (ADR-019) ===
  // Keyed by the org Holder id: the "workspace" IS the existing Vault org
  // Holder, so a membership change surfaced through `vault.me.memberships`
  // is a different key space on purpose — nothing here mirrors the roster.
  workspaceAssetLibrary: {
    all: ['contributor', 'workspaces'] as const,
    holder: (holderId: string) =>
      [...queryKeys.workspaceAssetLibrary.all, 'holder', holderId] as const,
    config: (holderId: string) =>
      [...queryKeys.workspaceAssetLibrary.holder(holderId), 'config'] as const,
    assets: (holderId: string, opts?: Record<string, unknown>) =>
      [...queryKeys.workspaceAssetLibrary.holder(holderId), 'assets', opts ?? {}] as const,
    manifests: (holderId: string) =>
      [...queryKeys.workspaceAssetLibrary.holder(holderId), 'manifests'] as const,
    arches: (holderId: string) =>
      [...queryKeys.workspaceAssetLibrary.holder(holderId), 'arches'] as const,
    usage: (holderId: string) =>
      [...queryKeys.workspaceAssetLibrary.holder(holderId), 'usage'] as const,
    billingSummary: (holderId: string) =>
      [...queryKeys.workspaceAssetLibrary.holder(holderId), 'billing-summary'] as const,
  },

  // === Learn About Fias ===
  learnAboutFias: {
    all: ['learnAboutFias'] as const,
    resources: () => [...queryKeys.learnAboutFias.all, 'resources'] as const,
    admin: () => [...queryKeys.learnAboutFias.all, 'admin'] as const,
    adminDocuments: (filters?: Record<string, unknown>) =>
      [...queryKeys.learnAboutFias.admin(), 'documents', filters] as const,
    adminDocument: (docId: string) =>
      [...queryKeys.learnAboutFias.admin(), 'document', docId] as const,
    adminStats: () => [...queryKeys.learnAboutFias.admin(), 'stats'] as const,
  },

  // === Models ===
  models: {
    all: ['models'] as const,
    list: () => [...queryKeys.models.all, 'list'] as const,
    definition: (modelId: string) => [...queryKeys.models.all, 'definition', modelId] as const,
    definitionByLookup: (provider: string, modelName: string) =>
      [...queryKeys.models.all, 'definition', provider, modelName] as const,
    imageModels: () => [...queryKeys.models.all, 'imageModels'] as const,
    textCapabilities: () => [...queryKeys.models.all, 'textCapabilities'] as const,
  },

  // === Conversations ===
  conversations: {
    all: ['conversations'] as const,
    list: (archeId: string) => [...queryKeys.conversations.all, 'list', archeId] as const,
    detail: (conversationId: string) =>
      [...queryKeys.conversations.all, 'detail', conversationId] as const,
  },

  // === Files & Storage ===
  files: {
    all: ['files'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.files.all, 'list', filters] as const,
    detail: (fileId: string) => [...queryKeys.files.all, 'detail', fileId] as const,
    downloadUrl: (fileId: string) => [...queryKeys.files.all, 'downloadUrl', fileId] as const,
  },

  // === Contact Book ===
  contactBook: {
    all: ['contact-book'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.contactBook.all, 'list', filters] as const,
    // Lookup of whether a given email is already a CRM contact (dedup).
    byEmail: (email: string) =>
      [
        ...queryKeys.contactBook.all,
        'by-email',
        String(email ?? '')
          .trim()
          .toLowerCase(),
      ] as const,
  },

  folders: {
    all: ['folders'] as const,
    list: (parentId?: string | null) => [...queryKeys.folders.all, 'list', parentId] as const,
    detail: (folderId: string) => [...queryKeys.folders.all, 'detail', folderId] as const,
    breadcrumb: (folderId: string) => [...queryKeys.folders.all, 'breadcrumb', folderId] as const,
  },

  storage: {
    all: ['storage'] as const,
    usage: () => [...queryKeys.storage.all, 'usage'] as const,
    operations: () => [...queryKeys.storage.all, 'operations'] as const,
  },

  // === DFE Avatars (storage) ===
  dfeAvatars: {
    all: ['dfeAvatars'] as const,
    groups: () => [...queryKeys.dfeAvatars.all, 'groups'] as const,
    files: (prefix: string) => [...queryKeys.dfeAvatars.all, 'files', prefix] as const,
  },

  // === DFE Icons (storage) ===
  dfeIcons: {
    all: ['dfeIcons'] as const,
    groups: () => [...queryKeys.dfeIcons.all, 'groups'] as const,
    files: (prefix: string) => [...queryKeys.dfeIcons.all, 'files', prefix] as const,
  },

  // === Litcraft Addressables (storage) ===
  litcraftAddressables: {
    all: ['litcraftAddressables'] as const,
    groups: () => [...queryKeys.litcraftAddressables.all, 'groups'] as const,
    files: (prefix: string) => [...queryKeys.litcraftAddressables.all, 'files', prefix] as const,
  },

  // Owned by the social-media arche; see
  // client/src/arche/arches/social-media/api/queryKeys.ts
  socialMedia: socialMediaQueryKeys,

  // Owned by the forge arche; see client/src/arche/arches/forge/api/queryKeys.ts
  forge: forgeQueryKeys,

  // === Todo Lists (W6 — Vault-resident; keyed by holderId) ===
  todoLists: {
    all: ['todoLists'] as const,
    details: () => [...queryKeys.todoLists.all, 'detail'] as const,
    detail: (holderId: string, listId: string) =>
      [...queryKeys.todoLists.details(), holderId, listId] as const,
    my: (holderId: string) => [...queryKeys.todoLists.all, 'my', holderId] as const,
    workspaceSummary: () => [...queryKeys.todoLists.all, 'workspaceSummary'] as const,
    /** Caller-scoped unread assignment notifications (spans all workspaces). */
    assignmentNotifications: () => [...queryKeys.todoLists.all, 'assignmentNotifications'] as const,
    sections: (holderId: string, listId: string) =>
      [...queryKeys.todoLists.detail(holderId, listId), 'sections'] as const,
    items: (holderId: string, listId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.todoLists.detail(holderId, listId), 'items', filters] as const,
    subtasks: (holderId: string, listId: string, parentItemId: string) =>
      [...queryKeys.todoLists.detail(holderId, listId), 'subtasks', parentItemId] as const,
    /** A task's checklist items (lightweight text + checkbox breakdown). */
    checklist: (holderId: string, listId: string, itemId: string) =>
      [...queryKeys.todoLists.detail(holderId, listId), 'checklist', itemId] as const,
    subtaskIndex: (holderId: string, listId: string) =>
      [...queryKeys.todoLists.detail(holderId, listId), 'subtaskIndex'] as const,
    /** All full sub-task rows for a list, grouped client-side under their parent. */
    subtasksAll: (holderId: string, listId: string) =>
      [...queryKeys.todoLists.detail(holderId, listId), 'subtasksAll'] as const,
    sectionAccess: (holderId: string, listId: string) =>
      [...queryKeys.todoLists.detail(holderId, listId), 'sectionAccess'] as const,
    /** Projects are workspace(holder)-scoped, shared across the holder's lists. */
    projects: (holderId: string) => [...queryKeys.todoLists.all, 'projects', holderId] as const,
    /** A source list's outbound cross-list move shortcuts. */
    links: (holderId: string, listId: string) =>
      [...queryKeys.todoLists.detail(holderId, listId), 'links'] as const,
    personalBoard: (holderId: string, listId: string) =>
      [...queryKeys.todoLists.detail(holderId, listId), 'personalBoard'] as const,
    /** A personal task's checklist items (private mirror of `checklist`). */
    personalChecklist: (holderId: string, listId: string, personalItemId: string) =>
      [
        ...queryKeys.todoLists.detail(holderId, listId),
        'personalChecklist',
        personalItemId,
      ] as const,
    completed: (holderId: string, listId: string) =>
      [...queryKeys.todoLists.detail(holderId, listId), 'completed'] as const,
    /** The caller's completed/cancelled PRIVATE items (Completed page → My tasks). */
    completedPersonal: (holderId: string, listId: string) =>
      [...queryKeys.todoLists.detail(holderId, listId), 'completedPersonal'] as const,
    reviewers: (holderId: string, listId: string) =>
      [...queryKeys.todoLists.detail(holderId, listId), 'reviewers'] as const,
    public: (slug: string) => [...queryKeys.todoLists.all, 'public', slug] as const,
    publicCompleted: (slug: string, limit?: number) =>
      [...queryKeys.todoLists.all, 'public', slug, 'completed', limit] as const,
  },

  // === Customer Support ===
  customerSupport: {
    all: ['customerSupport'] as const,
    tickets: () => [...queryKeys.customerSupport.all, 'tickets'] as const,
    ticketList: (filters?: Record<string, unknown>) =>
      [...queryKeys.customerSupport.tickets(), 'list', filters] as const,
    ticketDetail: (ticketId: string) => [...queryKeys.customerSupport.tickets(), ticketId] as const,
    ticketMessages: (ticketId: string) =>
      [...queryKeys.customerSupport.ticketDetail(ticketId), 'messages'] as const,
    faqs: (categoryId?: string) => [...queryKeys.customerSupport.all, 'faqs', categoryId] as const,
    faqSearch: (query: string) =>
      [...queryKeys.customerSupport.all, 'faqs', 'search', query] as const,
    categories: () => [...queryKeys.customerSupport.all, 'categories'] as const,
    admin: () => [...queryKeys.customerSupport.all, 'admin'] as const,
    /** Prefix for EVERY admin ticket list, whatever its filters. */
    adminTicketsAll: () => [...queryKeys.customerSupport.admin(), 'tickets'] as const,
    adminTickets: (filters?: Record<string, unknown>) =>
      [...queryKeys.customerSupport.adminTicketsAll(), filters] as const,
    adminTicketDetail: (ticketId: string) =>
      [...queryKeys.customerSupport.admin(), 'ticket', ticketId] as const,
    adminTicketMessages: (ticketId: string) =>
      [...queryKeys.customerSupport.adminTicketDetail(ticketId), 'messages'] as const,
    adminAnalytics: () => [...queryKeys.customerSupport.admin(), 'analytics'] as const,
    adminCategories: () => [...queryKeys.customerSupport.admin(), 'categories'] as const,
    adminCannedResponses: () => [...queryKeys.customerSupport.admin(), 'cannedResponses'] as const,
    adminTags: () => [...queryKeys.customerSupport.admin(), 'tags'] as const,
    adminUnreadCounts: () => [...queryKeys.customerSupport.admin(), 'unreadCounts'] as const,
    archeMeta: (ids: string[]) =>
      [...queryKeys.customerSupport.admin(), 'archeMeta', [...ids].sort()] as const,
    adminUserChat: (userId: string) =>
      [...queryKeys.customerSupport.admin(), 'userChat', userId] as const,
    adminChatMessages: (conversationId: string) =>
      [...queryKeys.customerSupport.admin(), 'chatMessages', conversationId] as const,
    shareTargetChannels: () =>
      [...queryKeys.customerSupport.admin(), 'shareTargetChannels'] as const,
    shareTargetPeople: (query: string) =>
      [...queryKeys.customerSupport.admin(), 'shareTargetPeople', query] as const,
  },

  // === Voice Studio ===
  voiceStudio: {
    all: ['voiceStudio'] as const,
    voiceModels: () => [...queryKeys.voiceStudio.all, 'voiceModels'] as const,
    platformVoices: () => [...queryKeys.voiceStudio.all, 'platformVoices'] as const,
  },

  // === Document Sign ===
  documentSign: {
    all: ['documentSign'] as const,
    envelopes: () => [...queryKeys.documentSign.all, 'envelopes'] as const,
    envelopeList: (filters: Record<string, unknown>) =>
      [...queryKeys.documentSign.envelopes(), 'list', filters] as const,
    envelope: (envelopeId: string) => [...queryKeys.documentSign.envelopes(), envelopeId] as const,
    files: (envelopeId: string) =>
      [...queryKeys.documentSign.envelope(envelopeId), 'files'] as const,
    recipients: (envelopeId: string) =>
      [...queryKeys.documentSign.envelope(envelopeId), 'recipients'] as const,
    fields: (envelopeId: string, fileId: string) =>
      [...queryKeys.documentSign.envelope(envelopeId), 'files', fileId, 'fields'] as const,
    events: (envelopeId: string) =>
      [...queryKeys.documentSign.envelope(envelopeId), 'events'] as const,
    signingSession: (token: string) => [...queryKeys.documentSign.all, 'signing', token] as const,
    signingBranding: (token: string) =>
      [...queryKeys.documentSign.all, 'signing', token, 'branding'] as const,
    recentRecipients: (holderId: string) =>
      [...queryKeys.documentSign.all, 'recent-recipients', holderId] as const,
    bulkBatch: (batchId: string) => [...queryKeys.documentSign.all, 'bulk-batch', batchId] as const,
    /** Every workspace's list of bulk sends (invalidate by this prefix). */
    bulkBatchLists: () => [...queryKeys.documentSign.all, 'bulk-batches'] as const,
    bulkBatches: (holderId: string) =>
      [...queryKeys.documentSign.all, 'bulk-batches', holderId] as const,
    features: () => [...queryKeys.documentSign.all, 'features'] as const,
  },

  // === Fias Transfer (token-addressed public download links over Vault docs) ===
  //
  // `publicLink` is keyed by the raw share token because that is the only
  // identifier an anonymous visitor has — they never see a linkId. It is
  // deliberately NOT nested under `all`: the public download page must not
  // share a cache subtree with the sender's dashboard, so a sender-side
  // invalidation cannot evict a visitor's session state (or vice versa).
  fiasTransfer: {
    all: ['fiasTransfer'] as const,
    spaces: (holderId: string) => [...queryKeys.fiasTransfer.all, 'spaces', holderId] as const,
    /**
     * One of the two lists a holder has. Nested under `spaces()` so a single
     * invalidation of that prefix refreshes both — archiving moves a row from
     * one list to the other, so refreshing only the one you were looking at
     * leaves the other stale.
     */
    spacesList: (holderId: string, archived: boolean) =>
      [...queryKeys.fiasTransfer.spaces(holderId), archived ? 'archived' : 'active'] as const,
    space: (spaceId: string) => [...queryKeys.fiasTransfer.all, 'space', spaceId] as const,
    linkActivity: (linkId: string) =>
      [...queryKeys.fiasTransfer.all, 'link', linkId, 'activity'] as const,
    quote: (spaceId: string, params: Record<string, unknown>) =>
      [...queryKeys.fiasTransfer.space(spaceId), 'quote', params] as const,
    pageStats: (linkId: string) =>
      [...queryKeys.fiasTransfer.all, 'link', linkId, 'pages'] as const,
    // Nested under space() so any space invalidation refreshes the Q&A inbox
    // and folder list for free.
    questions: (spaceId: string) =>
      [...queryKeys.fiasTransfer.space(spaceId), 'questions'] as const,
    folders: (spaceId: string) => [...queryKeys.fiasTransfer.space(spaceId), 'folders'] as const,
    scanStatus: (spaceId: string) =>
      [...queryKeys.fiasTransfer.space(spaceId), 'scanStatus'] as const,
    folderExposure: (folderId: string) =>
      [...queryKeys.fiasTransfer.all, 'folderExposure', folderId] as const,
    /** Holder-wide sparse exposure map, for the Vault browser's folder badges. */
    folderExposures: (holderId: string) =>
      [...queryKeys.fiasTransfer.all, 'folderExposures', holderId] as const,
  },
  fiasTransferPublic: {
    all: ['fiasTransferPublic'] as const,
    summary: (token: string) => [...queryKeys.fiasTransferPublic.all, 'summary', token] as const,
    manifest: (token: string) => [...queryKeys.fiasTransferPublic.all, 'manifest', token] as const,
    // Rendered pages are large (base64 PNGs) and per-viewer when watermarked —
    // keyed per document so one preview never evicts another.
    preview: (token: string, documentId: string) =>
      [...queryKeys.fiasTransferPublic.all, 'preview', token, documentId] as const,
    questions: (token: string) =>
      [...queryKeys.fiasTransferPublic.all, 'questions', token] as const,
  },

  // === Fias Leads (Phase 9 — Holder-scoped CRM on Vault substrate) ===
  fiasLeads: {
    all: ['fiasLeads'] as const,
    forHolder: (holderId: string) => [...queryKeys.fiasLeads.all, holderId] as const,
    companies: (holderId: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'companies'] as const,
    companiesList: (holderId: string, filters: Record<string, unknown>) =>
      [...queryKeys.fiasLeads.companies(holderId), 'list', filters] as const,
    // Nested under companies() so every existing create/update/archive
    // invalidation of the companies prefix refreshes refs for free.
    companyRefs: (holderId: string) =>
      [...queryKeys.fiasLeads.companies(holderId), 'refs'] as const,
    company: (holderId: string, companyId: string) =>
      [...queryKeys.fiasLeads.companies(holderId), companyId] as const,
    contacts: (holderId: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'contacts'] as const,
    contactsList: (holderId: string, filters: { companyId?: string; includeArchived?: boolean }) =>
      [...queryKeys.fiasLeads.contacts(holderId), 'list', filters] as const,
    contact: (holderId: string, contactId: string) =>
      [...queryKeys.fiasLeads.contacts(holderId), contactId] as const,
    relationships: (holderId: string, contactId: string) =>
      [...queryKeys.fiasLeads.contact(holderId, contactId), 'relationships'] as const,
    deals: (holderId: string) => [...queryKeys.fiasLeads.forHolder(holderId), 'deals'] as const,
    dealsList: (holderId: string, filters: Record<string, unknown>) =>
      [...queryKeys.fiasLeads.deals(holderId), 'list', filters] as const,
    deal: (holderId: string, dealId: string) =>
      [...queryKeys.fiasLeads.deals(holderId), dealId] as const,
    dealContacts: (holderId: string, dealId: string) =>
      [...queryKeys.fiasLeads.deals(holderId), dealId, 'contacts'] as const,
    pipelineSummary: (holderId: string, pipelineId: string, sort?: Record<string, unknown>) =>
      [...queryKeys.fiasLeads.deals(holderId), 'pipelineSummary', pipelineId, sort ?? {}] as const,
    pipelines: (holderId: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'pipelines'] as const,
    pipeline: (holderId: string, pipelineId: string) =>
      [...queryKeys.fiasLeads.pipelines(holderId), pipelineId] as const,
    tasks: (holderId: string) => [...queryKeys.fiasLeads.forHolder(holderId), 'tasks'] as const,
    tasksList: (holderId: string, filters: Record<string, unknown>) =>
      [...queryKeys.fiasLeads.tasks(holderId), 'list', filters] as const,
    task: (holderId: string, taskId: string) =>
      [...queryKeys.fiasLeads.tasks(holderId), taskId] as const,
    activities: (holderId: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'activities'] as const,
    activitiesList: (holderId: string, filters: Record<string, unknown>) =>
      [...queryKeys.fiasLeads.activities(holderId), 'list', filters] as const,
    activity: (holderId: string, activityId: string) =>
      [...queryKeys.fiasLeads.activities(holderId), activityId] as const,
    emailTemplates: (holderId: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'emailTemplates'] as const,
    emailTemplate: (holderId: string, templateId: string) =>
      [...queryKeys.fiasLeads.emailTemplates(holderId), templateId] as const,
    automations: (holderId: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'automations'] as const,
    automation: (holderId: string, ruleId: string) =>
      [...queryKeys.fiasLeads.automations(holderId), ruleId] as const,
    automationExecutions: (holderId: string, ruleId: string) =>
      [...queryKeys.fiasLeads.automation(holderId, ruleId), 'executions'] as const,
    customFields: (holderId: string, entityType?: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'customFields', entityType ?? '_any'] as const,
    savedViews: (holderId: string, entityType?: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'savedViews', entityType ?? '_any'] as const,
    dashboard: (holderId: string, pipelineId?: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'dashboard', pipelineId ?? '_default'] as const,
    dealRisk: (holderId: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'dealRisk'] as const,
    dealRiskAlerts: (holderId: string) =>
      [...queryKeys.fiasLeads.dealRisk(holderId), 'alerts'] as const,
    timeline: (holderId: string, filters: Record<string, unknown>) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'timeline', filters] as const,
    reports: (holderId: string) => [...queryKeys.fiasLeads.forHolder(holderId), 'reports'] as const,
    pipelineVelocity: (holderId: string, pipelineId?: string) =>
      [...queryKeys.fiasLeads.reports(holderId), 'pipelineVelocity', pipelineId ?? '_all'] as const,
    forecast: (holderId: string, pipelineId?: string) =>
      [...queryKeys.fiasLeads.reports(holderId), 'forecast', pipelineId ?? '_all'] as const,
    activityTrends: (holderId: string, days: number) =>
      [...queryKeys.fiasLeads.reports(holderId), 'activityTrends', days] as const,
    lostDeals: (holderId: string, pipelineId?: string) =>
      [...queryKeys.fiasLeads.reports(holderId), 'lostDeals', pipelineId ?? '_all'] as const,
    dedupContacts: (holderId: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'dedup', 'contacts'] as const,
    crossArchePresence: (holderId: string, contactIds: string[], companyIds: string[]) =>
      [
        ...queryKeys.fiasLeads.forHolder(holderId),
        'crossArchePresence',
        contactIds.sort().join(','),
        companyIds.sort().join(','),
      ] as const,
    search: (holderId: string, q: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'search', q] as const,
    importBatches: (holderId: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'importBatches'] as const,
  },

  // === Vault Notes (Phase 3 — first Vault Entity consumer) ===
  vaultNotes: {
    all: ['vaultNotes'] as const,
    lists: () => [...queryKeys.vaultNotes.all, 'list'] as const,
    list: (filters: { includeDeleted?: boolean; limit?: number }) =>
      [...queryKeys.vaultNotes.lists(), filters] as const,
    details: () => [...queryKeys.vaultNotes.all, 'detail'] as const,
    detail: (noteId: string) => [...queryKeys.vaultNotes.details(), noteId] as const,
  },

  /** Connections (ADR-029 §2): a workspace's connected outside accounts. */
  connections: {
    all: ['connections'] as const,
    providers: () => ['connections', 'providers'] as const,
    forHolder: (holderId: string) => ['connections', 'holder', holderId] as const,
  },
  /** Outward actions an app asked to take, awaiting the person (ADR-029 §4). */
  pluginActions: {
    all: ['plugin-actions'] as const,
    detail: (pendingActionId: string) => ['plugin-actions', pendingActionId] as const,
  },

  // === Vault — caller-side (membership picker, invitations) ===
  //
  // Folded in from previously module-local TEAM_KEY / MY_INVITES_KEY /
  // MY_MEMBERSHIPS_KEY constants in arche/arches/fias360/hooks/useVaultTeam.ts
  // and the inline `['vault','me','memberships']` literal in
  // components/HolderSwitcher.tsx. Centralizing here so the namespace
  // ownership is unambiguous and renames don't silently miss callers.
  vault: {
    all: ['vault'] as const,
    /** A Holder's Creative Library (ADR-037), by kind. */
    creative: (holderId: string, kind: 'style' | 'character') =>
      ['vault', 'creative', holderId, kind] as const,
    me: {
      memberships: () => ['vault', 'me', 'memberships'] as const,
      invitations: () => ['vault', 'me', 'invitations'] as const,
      companyLinkProposals: () => ['vault', 'me', 'company-link-proposals'] as const,
      /** "Your workspace joined a Company" — told to every member (ADR-022). */
      companyLinkNotices: () => ['vault', 'me', 'company-link-notices'] as const,
    },
    team: {
      all: ['vault', 'team'] as const,
      forHolder: (holderId: string) => ['vault', 'team', holderId] as const,
      members: (holderId: string) => ['vault', 'team', holderId, 'members'] as const,
      invitations: (holderId: string) => ['vault', 'team', holderId, 'invitations'] as const,
    },
    // ADR-022 — the workspace side of Companies + approvals. The `*All`
    // prefixes invalidate every workspace's view at once (a Company deleted).
    companyAll: ['vault', 'company'] as const,
    company: (holderId: string) => [...queryKeys.vault.companyAll, holderId] as const,
    approvalsAll: ['vault', 'approvals'] as const,
    approvals: (holderId: string) => [...queryKeys.vault.approvalsAll, holderId] as const,
    approvalActivity: (holderId: string, archeId: string) =>
      ['vault', 'approvals', holderId, 'activity', archeId] as const,
    accessDenials: (holderId: string) => ['vault', 'access-denials', holderId] as const,
    // ADR-034: one app's roles in one workspace.
    appAccessAll: (holderId: string, archeId: string) =>
      ['vault', 'app-access', holderId, archeId] as const,
    appAccess: (holderId: string, archeId: string) =>
      ['vault', 'app-access', holderId, archeId, 'screen'] as const,
    myAppAccess: (holderId: string, archeId: string) =>
      ['vault', 'app-access', holderId, archeId, 'mine'] as const,
    appAccessExplain: (holderId: string, archeId: string, principalId: string) =>
      ['vault', 'app-access', holderId, archeId, 'explain', principalId] as const,
    appAccessReview: (holderId: string, archeId: string) =>
      ['vault', 'app-access', holderId, archeId, 'review'] as const,
    // ADR-040 Q12: the app workspace the Add people sheet adds into.
    appWorkspaceForAddPeople: (archeId: string, workspaceId: string) =>
      ['vault', 'app-workspace-add-people', archeId, workspaceId] as const,
    // ADR-039: one workspace's named labels. `namedLabelsAll` is the prefix
    // every label write invalidates (a write changes the list, the readers,
    // the requests and the caller's own place at once).
    namedLabelsAll: (holderId: string) => ['vault', 'named-labels', holderId] as const,
    namedLabels: (holderId: string) => ['vault', 'named-labels', holderId, 'list'] as const,
    namedLabelReaders: (holderId: string, labelId: string) =>
      ['vault', 'named-labels', holderId, 'label', labelId, 'readers'] as const,
    namedLabelAccessLog: (holderId: string, labelId: string) =>
      ['vault', 'named-labels', holderId, 'label', labelId, 'access-log'] as const,
    namedLabelRequests: (holderId: string) =>
      ['vault', 'named-labels', holderId, 'requests'] as const,
    namedLabelReceipts: (holderId: string) =>
      ['vault', 'named-labels', holderId, 'receipts'] as const,
    namedLabelGroups: (holderId: string, labelId: string) =>
      ['vault', 'named-labels', holderId, 'label', labelId, 'groups'] as const,
    // Deliberately NOT under `namedLabelsAll`: every preview the server answers
    // is an audited look at who is in a group (ADR-041 §Visibility), so it is
    // fetched when the manager asks to look, never again because some other
    // label write on the page invalidated the prefix.
    namedLabelGroupPreview: (holderId: string, labelId: string, groupId: string) =>
      ['vault', 'named-label-group-preview', holderId, labelId, groupId] as const,
    // ADR-041: one workspace's groups. `workspaceGroupsAll` is the prefix every
    // group write invalidates; a group write also invalidates `namedLabelsAll`,
    // since it changes who reads the labels the group is on.
    workspaceGroupsAll: (holderId: string) => ['vault', 'workspace-groups', holderId] as const,
    workspaceGroups: (holderId: string) => ['vault', 'workspace-groups', holderId, 'list'] as const,
    workspaceGroupMembers: (holderId: string, groupId: string) =>
      ['vault', 'workspace-groups', holderId, 'group', groupId, 'members'] as const,
  },

  // === Companies (ADR-022) ===
  companies: {
    all: ['companies'] as const,
    mine: () => ['companies', 'mine'] as const,
    /** The caller's own open seat invitations (the invitee's side). */
    myInvitations: () => ['companies', 'invitations', 'mine'] as const,
    detail: (companyId: string) => ['companies', companyId] as const,
    approvals: (companyId: string) => ['companies', companyId, 'approvals'] as const,
    approvalActivity: (companyId: string, archeId: string) =>
      ['companies', companyId, 'approvals', 'activity', archeId] as const,
    billing: (companyId: string, month: string) =>
      ['companies', companyId, 'billing', month] as const,
    /** The Company's brand (ADR-023). */
    branding: (companyId: string) => ['companies', companyId, 'branding'] as const,
    /** Company email: domains, sender addresses, send-as authorizations (ADR-023). */
    email: (companyId: string) => ['companies', companyId, 'email'] as const,
    web: (companyId: string) => ['companies', companyId, 'web'] as const,
    webDomainRecords: (companyId: string, domainId: string) =>
      ['companies', companyId, 'web', 'domains', domainId, 'records'] as const,
    webDomainTransferQuote: (companyId: string, domainId: string) =>
      ['companies', companyId, 'web', 'domains', domainId, 'transfer-quote'] as const,
    webUsers: (companyId: string, hostId: string) =>
      ['companies', companyId, 'web', hostId, 'users'] as const,
    /** Typeahead in the approval dialog; scopeKey is the holder or Company searched on. */
    archeSearch: (scopeKey: string, q: string) =>
      ['companies', 'arche-search', scopeKey, q] as const,
    /** Company → Owned arches: owned arches + transfer proposals (ADR-026). */
    arches: (companyId: string) => ['companies', companyId, 'arches'] as const,
    archeTransferDisclosure: (companyId: string, transferId: string) =>
      ['companies', companyId, 'arches', 'transfer', transferId] as const,
  },

  // === Arche ownership proposals + shared arches, across arches (ADR-026) ===
  // One arche's ownership and audience live under contributorArches.detail.
  archeGovernance: {
    all: ['arche-governance'] as const,
    myTransfers: () => ['arche-governance', 'transfers', 'mine'] as const,
    transferCompanies: () => ['arche-governance', 'transfers', 'companies'] as const,
    sharedWithMe: () => ['arche-governance', 'shared-with-me'] as const,
  },

  // === Platform user directory (GET /users/search autocomplete) ===
  users: {
    all: ['users'] as const,
    search: (query: string) => ['users', 'search', query] as const,
  },

  // === Fias 360 (Phase 10 — SMB CRM arche) ===
  fias360: {
    all: ['fias360'] as const,
    customers: (holderId: string) => [...queryKeys.fias360.all, 'customers', holderId] as const,
    /** Filtered list — separate cache slot per filter object so a
     * companyId-filtered query on AccountDetailPage doesn't poison the
     * unfiltered list on the Members tab. Invalidation can still use
     * `customers(holderId)` as a prefix to clear all filter variants. */
    customersList: (holderId: string, filters: { companyId?: string; includeArchived?: boolean }) =>
      [...queryKeys.fias360.all, 'customers', holderId, 'list', filters] as const,
    customer: (holderId: string, customerId: string) =>
      [...queryKeys.fias360.customers(holderId), customerId] as const,
    companies: (holderId: string) => [...queryKeys.fias360.all, 'companies', holderId] as const,
    company: (holderId: string, companyId: string) =>
      [...queryKeys.fias360.companies(holderId), companyId] as const,
    timeline: (
      holderId: string,
      filters: {
        regardingType?: string;
        regardingId?: string;
        kind?: string;
        limit?: number;
      },
    ) => [...queryKeys.fias360.all, 'timeline', holderId, filters] as const,
    health: (holderId: string, subjectKind: string, subjectId: string) =>
      [...queryKeys.fias360.all, 'health', holderId, subjectKind, subjectId] as const,
    renewals: (holderId: string, filters: { companyId?: string; status?: string }) =>
      [...queryKeys.fias360.all, 'renewals', holderId, filters] as const,
    tasks: (holderId: string) => [...queryKeys.fias360.all, 'tasks', holderId] as const,
    tasksList: (holderId: string, filters: Record<string, unknown>) =>
      [...queryKeys.fias360.tasks(holderId), 'list', filters] as const,
    task: (holderId: string, taskId: string) =>
      [...queryKeys.fias360.tasks(holderId), taskId] as const,
    // Segments — folded in from a module-local SEGMENTS_KEY_BASE
    // in useFias360.ts. Same pattern as the customers/companies
    // namespaces: per-Holder root, filtered list, individual detail.
    segments: (holderId: string) => [...queryKeys.fias360.all, 'segments', holderId] as const,
    segmentsList: (holderId: string, options?: Record<string, unknown>) =>
      [...queryKeys.fias360.segments(holderId), options] as const,
    segment: (holderId: string, segmentId: string) =>
      [...queryKeys.fias360.segments(holderId), 'detail', segmentId] as const,
    segmentApply: (holderId: string, segmentId: string) =>
      [...queryKeys.fias360.segments(holderId), 'apply', segmentId] as const,
    // Reports — folded in from REPORTS_KEY_BASE.
    reports: (holderId: string) => [...queryKeys.fias360.all, 'reports', holderId] as const,
    reportCustomersByLifecycle: (holderId: string) =>
      [...queryKeys.fias360.reports(holderId), 'customers-by-lifecycle-stage'] as const,
    reportActivityVolume: (holderId: string, days: number) =>
      [...queryKeys.fias360.reports(holderId), 'activity-volume-by-actor', days] as const,
    reportUncontactedCustomers: (holderId: string, days: number) =>
      [...queryKeys.fias360.reports(holderId), 'uncontacted-customers', days] as const,
    reportNewCustomers: (holderId: string, days: number) =>
      [...queryKeys.fias360.reports(holderId), 'new-customers-over-time', days] as const,
    reportRenewalsByStatus: (holderId: string) =>
      [...queryKeys.fias360.reports(holderId), 'renewals-by-status'] as const,
    // Custom fields — folded in from CUSTOM_FIELDS_KEY_BASE.
    customFields: (holderId: string) =>
      [...queryKeys.fias360.all, 'custom-fields', holderId] as const,
    customFieldDefs: (holderId: string, options?: Record<string, unknown>) =>
      [...queryKeys.fias360.customFields(holderId), 'defs', options] as const,
    customFieldDefsRoot: (holderId: string) =>
      [...queryKeys.fias360.customFields(holderId), 'defs'] as const,
    customFieldValuesForCustomer: (holderId: string, customerId: string) =>
      [...queryKeys.fias360.customFields(holderId), 'values', 'customer', customerId] as const,
    customFieldValuesForCompany: (holderId: string, companyId: string) =>
      [...queryKeys.fias360.customFields(holderId), 'values', 'company', companyId] as const,
    // Workspace preferences — folded in from WORKSPACE_PREFS_KEY_BASE.
    workspacePreferences: (holderId: string) =>
      [...queryKeys.fias360.all, 'workspace-preferences', holderId] as const,
  },

  // === Vault Documents (Phase 12 — Holder-scoped document storage) ===
  vaultDocuments: {
    all: ['vaultDocuments'] as const,
    /** Per-Holder root. Use as the invalidation prefix when anything
     * about a Holder's document corpus changes (upload finalize,
     * archive, delete, restore, sensitivity change). */
    forHolder: (holderId: string) => [...queryKeys.vaultDocuments.all, holderId] as const,
    /** Filtered list — separate cache slot per filter object so an
     * archive-tab listing doesn't poison the default active list. */
    list: (
      holderId: string,
      filters: {
        status?: string;
        tags?: string[];
        includeArchived?: boolean;
        includeDeleted?: boolean;
        deletedOnly?: boolean;
        folderId?: string | null;
        search?: string;
        kinds?: readonly string[];
        areas?: readonly string[];
        minSizeBytes?: number;
        maxSizeBytes?: number;
        createdAfter?: string;
        createdBefore?: string;
        updatedAfter?: string;
        updatedBefore?: string;
        sourceArche?: string;
        sort?: string;
        direction?: string;
      },
    ) => [...queryKeys.vaultDocuments.forHolder(holderId), 'list', filters] as const,
    /** The workspace's By Type / By Arche summaries — same filters as the
     *  list, under `forHolder` so any write refreshes them with it. */
    groups: (holderId: string, by: 'kind' | 'arche', filters: Record<string, unknown>) =>
      [...queryKeys.vaultDocuments.forHolder(holderId), 'groups', by, filters] as const,
    /** Folder tree for a Holder. Single cache slot per Holder — the
     * tree is small enough that we always fetch the whole flat list
     * and assemble the tree client-side. */
    folders: (holderId: string) =>
      [...queryKeys.vaultDocuments.forHolder(holderId), 'folders'] as const,
    /** The host Open / Save dialogs' paged listing — one slot per query
     *  (location, search, kinds, sort). Under `forHolder`, so any write to
     *  the Holder's documents refreshes an open dialog too. */
    fileDialog: (
      holderId: string,
      query: {
        areas?: readonly string[];
        folderId?: string | null;
        search?: string;
        kinds?: readonly string[];
        sort?: string;
        direction?: string;
        forArche?: boolean;
      },
    ) => [...queryKeys.vaultDocuments.forHolder(holderId), 'fileDialog', query] as const,
    /** One person's Desktop arrangement for a Holder. Deliberately NOT
     *  under `forHolder`: document writes don't change it, so they shouldn't
     *  refetch it. */
    desktopLayout: (holderId: string) =>
      [...queryKeys.vaultDocuments.all, 'desktopLayout', holderId] as const,
    details: () => [...queryKeys.vaultDocuments.all, 'detail'] as const,
    detail: (documentId: string) => [...queryKeys.vaultDocuments.details(), documentId] as const,
    versions: (documentId: string) =>
      [...queryKeys.vaultDocuments.detail(documentId), 'versions'] as const,
    /** The file's history choice and cost (file areas §4.12). */
    history: (documentId: string) =>
      [...queryKeys.vaultDocuments.detail(documentId), 'history'] as const,
    /** A workspace's version-history default. */
    fileSettings: (holderId: string) =>
      [...queryKeys.vaultDocuments.forHolder(holderId), 'fileSettings'] as const,
    /** Search results — keyed by the query so a re-search with the
     * same string hits the cache. Frontend invalidates this whole
     * branch on any doc write. */
    search: (holderId: string, params: { query: string; topK?: number; minSimilarity?: number }) =>
      [...queryKeys.vaultDocuments.forHolder(holderId), 'search', params] as const,
    /** Per-Holder storage rollup (review fix #9). */
    storage: (holderId: string) =>
      [...queryKeys.vaultDocuments.forHolder(holderId), 'storage'] as const,
    /** What is using the storage, by area and arche (file areas §4.9). */
    storageBreakdown: (holderId: string) =>
      [...queryKeys.vaultDocuments.forHolder(holderId), 'storageBreakdown'] as const,
    /** What arches keep for the user outside their files (file areas §4.9). */
    archeData: (holderId: string) =>
      [...queryKeys.vaultDocuments.forHolder(holderId), 'archeData'] as const,
    /** Apps that hold a grant on this document (read or edit). */
    appAccess: (documentId: string) =>
      [...queryKeys.vaultDocuments.detail(documentId), 'appAccess'] as const,
    /** Every app grant the user has given, across documents (Vault → App
     * Access). Under `all`, so a grant made from a host consent sheet — which
     * invalidates the whole branch — refreshes it too. */
    appAccessLists: () => [...queryKeys.vaultDocuments.all, 'appAccessList'] as const,
    appAccessList: (holderId: string) =>
      [...queryKeys.vaultDocuments.appAccessLists(), holderId] as const,
    /** Who changed this file — the per-document audit trail. */
    activity: (documentId: string) =>
      [...queryKeys.vaultDocuments.detail(documentId), 'activity'] as const,
    /** Cross-arche attachment views (Phase 12e-1). */
    attachmentsForDocument: (documentId: string) =>
      [...queryKeys.vaultDocuments.detail(documentId), 'attachments'] as const,
    attachmentsForTarget: (regardingType: string, regardingId: string) =>
      [
        ...queryKeys.vaultDocuments.all,
        'attachmentsForTarget',
        regardingType,
        regardingId,
      ] as const,
  },

  // === Email (external IMAP/POP3/SMTP client — Vault-resident) ===
  //
  // Two surfaces under one root: `connections` (the connected-mailbox list)
  // and `mailbox` (folders/threads/messages/search/compose-assets/screener…).
  // Keys are hierarchical so a mutation can invalidate broadly (`mailbox.all`,
  // `mailbox.messages()`) or narrowly (`mailbox.messageList(...)`); the optimistic
  // message-list patches use `mailbox.messages()` as the partial-match prefix.
  email: {
    all: ['email'] as const,
    // Parsed .ics invite preview. Deliberately NOT under `mailbox.all`: the
    // optimistic write helpers cancel that whole prefix, which would abort an
    // in-flight preview on any star/archive and leave the inline card missing.
    calendarInvite: (messageId: string, attachmentId: string) =>
      ['email', 'calendar-invite', messageId, attachmentId] as const,

    connections: {
      all: ['email', 'connections'] as const,
      list: () => [...queryKeys.email.connections.all, 'list'] as const,
    },

    // Holder-wide (cross-account) global settings — preview length, swipe
    // gestures, Scheduled/Snooze opt-ins. One shared cache entry.
    userSettings: () => ['email', 'user-settings'] as const,

    mailbox: {
      all: ['email', 'mailbox'] as const,
      folders: (accountId: string) =>
        [...queryKeys.email.mailbox.all, 'folders', accountId] as const,
      // Root prefix for every thread-list query (any account/folder).
      threads: () => [...queryKeys.email.mailbox.all, 'threads'] as const,
      threadList: (accountId: string, folderId?: string) =>
        [...queryKeys.email.mailbox.threads(), accountId, folderId ?? 'all'] as const,
      // Root prefix for every message-list query (any account/folder/thread).
      messages: () => [...queryKeys.email.mailbox.all, 'messages'] as const,
      messageList: (
        accountId: string,
        folderId?: string,
        threadId?: string,
        screener = false,
        limit?: number,
        filters?: {
          isRead?: boolean;
          isStarred?: boolean;
          hasAttachments?: boolean;
          categoryId?: string;
          labelId?: string;
          /** Unified inbox: which accounts the screener applies to (part of the
           *  key so toggling the screener on an account re-fetches the merged list). */
          screenerAccountIds?: string[];
          /** The dedicated Screener inbox (held mail only) — a distinct cache entry. */
          screenerView?: boolean;
          /** "More filters": reply state, address-book membership, age bounds.
           *  In the key like every other filter, so changing one re-fetches. */
          answered?: boolean;
          inAddressBook?: boolean;
          receivedAfter?: string;
          receivedBefore?: string;
        },
      ) =>
        [
          ...queryKeys.email.mailbox.messages(),
          accountId,
          folderId ?? 'all',
          threadId ?? 'all',
          screener,
          limit ?? 'all',
          filters && Object.keys(filters).length > 0 ? JSON.stringify(filters) : 'no-filters',
        ] as const,
      message: (messageId: string) =>
        [...queryKeys.email.mailbox.all, 'message', messageId] as const,
      attachments: (messageId: string) =>
        [...queryKeys.email.mailbox.all, 'attachments', messageId] as const,
      // Nested UNDER the `messages()` prefix (not just `all`) so the optimistic
      // write patches and the post-write `reconcile()` invalidation — both keyed
      // on `messages()` — also cover the search results. Otherwise a bulk action
      // (mark-read/star/move/delete) taken while searching writes to the server
      // but never updates the visible search list until the next re-search.
      search: (
        accountId: string | undefined,
        query: string,
        fields: readonly string[] = [],
        folderId: string = 'all',
        filters?: {
          isRead?: boolean;
          isStarred?: boolean;
          hasAttachments?: boolean;
          answered?: boolean;
          inAddressBook?: boolean;
          receivedAfter?: string;
          receivedBefore?: string;
        },
      ) =>
        [
          ...queryKeys.email.mailbox.messages(),
          'search',
          accountId ?? 'all',
          query,
          [...fields].sort().join(',') || 'all',
          folderId,
          filters && Object.keys(filters).length > 0 ? JSON.stringify(filters) : 'no-filters',
        ] as const,
      // Advanced (nested AND/OR) search — keyed by the serialized query tree and
      // the chosen account subset (sorted so key identity is order-independent).
      // Under `messages()` so a bulk write's reconcile also refetches it.
      advancedSearch: (accountIds: string[] | undefined, folderId: string, query: unknown) =>
        [
          ...queryKeys.email.mailbox.messages(),
          'advanced-search',
          accountIds && accountIds.length > 0 ? [...accountIds].sort().join(',') : 'all',
          folderId,
          JSON.stringify(query),
        ] as const,
      // Nested UNDER `messages()` so a bulk write's `reconcile()` (keyed on
      // `messages()`) also refetches the active by-sender overview — moving all
      // of a sender's mail updates the cards without a manual refresh.
      bySender: (
        accountId: string,
        folderId: string = 'all',
        opts?: {
          excludeStarred?: boolean;
          excludeUnread?: boolean;
          excludeAttachments?: boolean;
          answered?: boolean;
          inAddressBook?: boolean;
          receivedAfter?: string;
          receivedBefore?: string;
          /** Search narrowing, pre-serialized (`query|fields`). */
          search?: string;
        },
      ) =>
        [
          ...queryKeys.email.mailbox.messages(),
          'by-sender',
          accountId,
          folderId,
          opts?.excludeStarred ? 'no-starred' : 'all-starred',
          opts?.excludeUnread ? 'no-unread' : 'all-unread',
          opts?.excludeAttachments ? 'no-attachments' : 'all-attachments',
          advancedFilterKey(opts),
          opts?.search ?? 'no-search',
        ] as const,
      // Nested UNDER `messages()` (like `bySender`) so a bulk write's
      // `reconcile()` also refetches the active by-category overview.
      byCategory: (
        accountId: string,
        folderId: string = 'all',
        opts?: {
          excludeStarred?: boolean;
          excludeUnread?: boolean;
          excludeAttachments?: boolean;
          answered?: boolean;
          inAddressBook?: boolean;
          receivedAfter?: string;
          receivedBefore?: string;
          /** Search narrowing, pre-serialized (`query|fields`). */
          search?: string;
        },
      ) =>
        [
          ...queryKeys.email.mailbox.messages(),
          'by-category',
          accountId,
          folderId,
          opts?.excludeStarred ? 'no-starred' : 'all-starred',
          opts?.excludeUnread ? 'no-unread' : 'all-unread',
          opts?.excludeAttachments ? 'no-attachments' : 'all-attachments',
          advancedFilterKey(opts),
          opts?.search ?? 'no-search',
        ] as const,
      scheduled: () => [...queryKeys.email.mailbox.all, 'scheduled'] as const,
      snoozed: () => [...queryKeys.email.mailbox.all, 'snoozed-messages'] as const,
      snoozedList: (accountId: string) =>
        [...queryKeys.email.mailbox.snoozed(), accountId] as const,
      signatures: (accountId: string) =>
        [...queryKeys.email.mailbox.all, 'signatures', accountId] as const,
      templates: (accountId: string) =>
        [...queryKeys.email.mailbox.all, 'templates', accountId] as const,
      snippets: (accountId: string) =>
        [...queryKeys.email.mailbox.all, 'snippets', accountId] as const,
      filters: (accountId: string) =>
        [...queryKeys.email.mailbox.all, 'filters', accountId] as const,
      // Root prefix for an account's contacts (invalidates every search variant).
      contacts: (accountId?: string) =>
        [...queryKeys.email.mailbox.all, 'contacts', accountId] as const,
      contactList: (accountId: string, search: string) =>
        [...queryKeys.email.mailbox.contacts(accountId), search] as const,
      // Root prefix for an account's screener (invalidates every status variant).
      screener: (accountId: string) =>
        [...queryKeys.email.mailbox.all, 'screener', accountId] as const,
      screenerList: (accountId: string, status?: string) =>
        [...queryKeys.email.mailbox.screener(accountId), status] as const,
      // Latest email from a screened sender, loaded on demand for the preview popup.
      screenerPreview: (accountId: string, senderAddress: string) =>
        [...queryKeys.email.mailbox.screener(accountId), 'preview', senderAddress] as const,
      // AI spam filter — per-account settings + whitelist/blacklist entries.
      spamSettings: (accountId: string) =>
        [...queryKeys.email.mailbox.all, 'spam-settings', accountId] as const,
      // User-wide (not per-account) AI spam-filter spend by period.
      spamSpend: () => [...queryKeys.email.mailbox.all, 'spam-spend'] as const,
      vacationSettings: (accountId: string) =>
        [...queryKeys.email.mailbox.all, 'vacation-settings', accountId] as const,
      // Paged allow/block list (one mailbox or all). Under `all` so a report
      // spam / add / remove refreshes every open page.
      spamSenderPages: (params: {
        disposition: string;
        accountId?: string;
        decidedBy?: string;
        q?: string;
      }) =>
        [
          ...queryKeys.email.mailbox.all,
          'spam-sender-pages',
          params.disposition,
          params.accountId ?? 'all',
          params.decidedBy ?? 'any',
          params.q ?? '',
        ] as const,
      spamTrustSummary: (accountId?: string) =>
        [...queryKeys.email.mailbox.all, 'spam-trust', accountId ?? 'all'] as const,
    },
  },

  // === Travel ===
  travel: {
    all: ['travel'] as const,
    /**
     * Inert placeholder for a query rendered without its id (always paired
     * with `enabled: false`). Keeps disabled branches on the factory instead
     * of hand-built raw string arrays that evade the query-key discipline.
     */
    disabled: (scope: string) => [...queryKeys.travel.all, scope, 'none'] as const,
    my: () => [...queryKeys.travel.all, 'my'] as const,
    details: () => [...queryKeys.travel.all, 'detail'] as const,
    detail: (tripId: string) => [...queryKeys.travel.details(), tripId] as const,
    profile: () => [...queryKeys.travel.all, 'profile'] as const,
    features: () => [...queryKeys.travel.all, 'features'] as const,
    dossier: (tripId: string) => [...queryKeys.travel.all, 'dossier', tripId] as const,
    inbox: (filters?: Record<string, unknown>) =>
      [...queryKeys.travel.all, 'inbox', filters] as const,
    flightStatus: (itemId: string) => [...queryKeys.travel.all, 'flightStatus', itemId] as const,
    budget: (tripId: string) => [...queryKeys.travel.all, 'budget', tripId] as const,
    budgetSummary: (tripId: string) => [...queryKeys.travel.all, 'budgetSummary', tripId] as const,
    expenses: (tripId: string) => [...queryKeys.travel.all, 'expenses', tripId] as const,
    /**
     * Prefix covering every todos query for a trip regardless of category
     * filter — use for invalidation. `todos(tripId)` resolves to
     * [..., 'todos', tripId, null], which does NOT prefix-match
     * [..., 'todos', tripId, 'packing'], so invalidating it misses the
     * category-filtered panels.
     */
    todosByTrip: (tripId: string) => [...queryKeys.travel.all, 'todos', tripId] as const,
    todos: (tripId: string, category?: string) =>
      [...queryKeys.travel.todosByTrip(tripId), category ?? null] as const,
    reservationSuggestions: (tripId: string) =>
      [...queryKeys.travel.all, 'reservationSuggestions', tripId] as const,
    tripInfo: (tripId: string) => [...queryKeys.travel.all, 'tripInfo', tripId] as const,
  },

  // === Calendar Sync ===
  // The user's Vault calendars (the calendar arche's collections), read by
  // other arches — e.g. the email arche's invite-calendar picker.
  calendarCollections: {
    all: ['calendarCollections'] as const,
  },
  calendarSync: {
    all: ['calendarSync'] as const,
    connections: () => [...queryKeys.calendarSync.all, 'connections'] as const,
    remoteCalendars: (connectionId: string) =>
      [...queryKeys.calendarSync.all, 'remoteCalendars', connectionId] as const,
    jobs: () => [...queryKeys.calendarSync.all, 'jobs'] as const,
    job: (jobId: string) => [...queryKeys.calendarSync.jobs(), jobId] as const,
  },

  // === Calendar Published Feeds (outbound webcal) ===
  calendarFeeds: {
    all: ['calendarFeeds'] as const,
    forCalendar: (calendarId: string) =>
      [...queryKeys.calendarFeeds.all, 'forCalendar', calendarId] as const,
  },

  // === Contact Sync ===
  contactSync: {
    all: ['contactSync'] as const,
    connections: () => [...queryKeys.contactSync.all, 'connections'] as const,
    remoteGroups: (connectionId: string) =>
      [...queryKeys.contactSync.all, 'remoteGroups', connectionId] as const,
    jobs: () => [...queryKeys.contactSync.all, 'jobs'] as const,
    job: (jobId: string) => [...queryKeys.contactSync.jobs(), jobId] as const,
  },

  // === User Credits (Credits page) ===
  credits: {
    all: ['credits'] as const,
    spendByArche: () => [...queryKeys.credits.all, 'spendByArche'] as const,
  },

  // === Admin Credit Activity (dev-console Credit Activity page) ===
  adminCredits: {
    all: ['adminCredits'] as const,
    activity: (kind: 'grants' | 'refunds', filters: Record<string, unknown>) =>
      [...queryKeys.adminCredits.all, 'activity', kind, filters] as const,
  },

  // === Admin Roles (dev-console Roles pages) ===
  adminRoles: {
    all: ['adminRoles'] as const,
    catalogue: () => [...queryKeys.adminRoles.all, 'catalogue'] as const,
    // No cursor in the key: members are paged with useInfiniteQuery, which owns
    // its own page params. Keying by cursor made each page a SEPARATE cache
    // entry, so advancing the cursor replaced the roster instead of extending
    // it — the first page simply vanished.
    members: (role: string) => [...queryKeys.adminRoles.all, 'members', role] as const,
  },

  // === MFA (account-settings status card) ===
  /** The account's passkeys, every site (ADR-024 §10). */
  passkeys: {
    all: ['passkeys'] as const,
    list: () => [...queryKeys.passkeys.all, 'list'] as const,
  },
  mfa: {
    all: ['mfa'] as const,
    status: () => [...queryKeys.mfa.all, 'status'] as const,
  },

  // === Company portals the user continued to (account-settings card; ADR-024 S14) ===
  companyPortals: {
    all: ['companyPortals'] as const,
    mine: () => [...queryKeys.companyPortals.all, 'mine'] as const,
    /** What the host this page is on is (a portal's profile, or the platform). */
    hostProfile: () => [...queryKeys.companyPortals.all, 'hostProfile'] as const,
    /** What the portal's own sign-in page offers (W5c). */
    signInOptions: () => [...queryKeys.companyPortals.all, 'signInOptions'] as const,
  },

  // === Company portal sign-in on fias.io (ADR-024) ===
  // Outside `companyPortals` so leaving a portal doesn't refetch these.
  companyPortalSignIn: {
    all: ['companyPortalSignIn'] as const,
    /** A portal's public brand, for the fias.io pages that sign in to it. */
    hostBrand: (hostId: string) =>
      [...queryKeys.companyPortalSignIn.all, 'hostBrand', hostId] as const,
    /** What the Continue step shows — per signed-in user, so switching accounts never flashes the last one's view. */
    authorize: (hostId: string, userId: string, search: string) =>
      [...queryKeys.companyPortalSignIn.all, 'authorize', hostId, userId, search] as const,
  },

  // === API keys (account-settings card; external agents / MCP) ===
  apiKeys: {
    all: ['apiKeys'] as const,
    lists: () => [...queryKeys.apiKeys.all, 'list'] as const,
    /** Workspace governance view — keys bound to one Holder (owner/admin). */
    forWorkspace: (holderId: string) => [...queryKeys.apiKeys.all, 'workspace', holderId] as const,
    /** Apps that share data with agents, in which the user keeps data (ADR-025). */
    agentApps: () => [...queryKeys.apiKeys.all, 'agentApps'] as const,
    /** One key's app grants. */
    appGrants: (keyId: string) => [...queryKeys.apiKeys.all, 'appGrants', keyId] as const,
  },

  // === Fias AI assistant (memory manager) ===
  fiasAI: {
    all: ['fiasAI'] as const,
    memory: () => [...queryKeys.fiasAI.all, 'memory'] as const,
    schedules: (scope: 'active' | 'history') =>
      [...queryKeys.fiasAI.all, 'schedules', scope] as const,
    scheduleExecutions: (id: string) =>
      [...queryKeys.fiasAI.all, 'schedules', 'executions', id] as const,
    scheduledAlertCount: () => [...queryKeys.fiasAI.all, 'scheduled-alert-count'] as const,
  },

  // === Admin Providers (dev-console Providers page) ===
  adminProviders: {
    all: ['adminProviders'] as const,
    overview: () => [...queryKeys.adminProviders.all, 'overview'] as const,
    detail: (provider: string) => [...queryKeys.adminProviders.all, 'detail', provider] as const,
    awsCosts: (period: string) => [...queryKeys.adminProviders.all, 'awsCosts', period] as const,
    spendHistory: (from: string, to: string) =>
      [...queryKeys.adminProviders.all, 'spendHistory', from, to] as const,
    health: (window: string) => [...queryKeys.adminProviders.all, 'health', window] as const,
    issueCount: () => [...queryKeys.adminProviders.all, 'issueCount'] as const,
    modelHealth: (provider: string, window: string) =>
      [...queryKeys.adminProviders.all, 'modelHealth', provider, window] as const,
    status: (provider: string) => [...queryKeys.adminProviders.all, 'status', provider] as const,
  },

  // === Admin web sources (dev-console, ADR-038): approved sites + the switch ===
  adminWebSources: {
    all: ['adminWebSources'] as const,
    sites: (params: { search: string; category: string; status: string; offset: number }) =>
      [...queryKeys.adminWebSources.all, 'sites', params] as const,
    switch: () => [...queryKeys.adminWebSources.all, 'switch'] as const,
    reader: () => [...queryKeys.adminWebSources.all, 'reader'] as const,
  },

  // === Admin Guest Mode (dev-console Platform Usage → Guest trials) ===
  adminGuestMode: {
    all: ['adminGuestMode'] as const,
    state: () => [...queryKeys.adminGuestMode.all, 'state'] as const,
  },

  // === Admin Forge (dev-console Forge page, ADR-023) ===
  /** The two arche governance kill switches (ADR-026), Dev Console → Arche Management. */
  adminArcheGovernance: {
    all: ['adminArcheGovernance'] as const,
    settings: () => [...queryKeys.adminArcheGovernance.all, 'settings'] as const,
    appRolesRollout: () => [...queryKeys.adminArcheGovernance.all, 'appRolesRollout'] as const,
    attestedCollectionsRollout: () =>
      [...queryKeys.adminArcheGovernance.all, 'attestedCollectionsRollout'] as const,
    vaultNamedLabelsRollout: () =>
      [...queryKeys.adminArcheGovernance.all, 'vaultNamedLabelsRollout'] as const,
  },
  adminForge: {
    all: ['adminForge'] as const,
    settings: () => [...queryKeys.adminForge.all, 'settings'] as const,
    metering: () => [...queryKeys.adminForge.all, 'metering'] as const,
    metrics: () => [...queryKeys.adminForge.all, 'metrics'] as const,
    forges: (filters: { status?: string; userId?: string; limit?: number; offset?: number }) =>
      [...queryKeys.adminForge.all, 'forges', filters] as const,
    forge: (forgeId: string) => [...queryKeys.adminForge.all, 'forge', forgeId] as const,
    holds: () => [...queryKeys.adminForge.all, 'holds'] as const,
  },
} as const;
